// src/utils/lookOnlyFirestore.js
// The app's Firestore and Storage WRITE calls, each asking utils/lookOnly.js first.
//
// Every file in src that saves to Firestore or uploads to Storage takes these calls from
// here instead of straight from "firebase/firestore" / "firebase/storage" (09/28/2026).
// Outside the owner's look-only person view they are exactly Firebase's own calls. In it
// they refuse with a LookOnlyError before anything is sent, so no screen, however it
// saves, can write in that view. Reads are untouched and still come from Firebase.
import {
  setDoc as fsSetDoc,
  updateDoc as fsUpdateDoc,
  addDoc as fsAddDoc,
  deleteDoc as fsDeleteDoc,
  writeBatch as fsWriteBatch,
  runTransaction as fsRunTransaction,
} from "firebase/firestore";
import {
  uploadBytes as stUploadBytes,
  uploadBytesResumable as stUploadBytesResumable,
  uploadString as stUploadString,
  deleteObject as stDeleteObject,
} from "firebase/storage";
import { assertCanWrite, isLookOnly, lookOnlyRejection } from "./lookOnly";

// Promise-returning writes refuse with a rejected promise, like a failed save would.
const guardAsync = (fn) => (...args) => (isLookOnly() ? lookOnlyRejection() : fn(...args));

export const setDoc = guardAsync((...a) => fsSetDoc(...a));
export const updateDoc = guardAsync((...a) => fsUpdateDoc(...a));
export const addDoc = guardAsync((...a) => fsAddDoc(...a));
export const deleteDoc = guardAsync((...a) => fsDeleteDoc(...a));
export const runTransaction = guardAsync((...a) => fsRunTransaction(...a));
export const uploadBytes = guardAsync((...a) => stUploadBytes(...a));
export const uploadString = guardAsync((...a) => stUploadString(...a));
export const deleteObject = guardAsync((...a) => stDeleteObject(...a));

// A batch is built before it is saved, so the refusal comes when it is started.
export const writeBatch = (...args) => {
  assertCanWrite();
  return fsWriteBatch(...args);
};

// An upload task is returned at once and watched, so it refuses when it is started.
export const uploadBytesResumable = (...args) => {
  assertCanWrite();
  return stUploadBytesResumable(...args);
};
